import React, { useState, useEffect } from "react";
import { Input, Select, Button } from "antd";
import styles from "./Filters.module.css";
import { API_BASE_URL } from "configs/AppConfig";
import { useLocation, useNavigate } from "react-router-dom";

const { Option } = Select;

// Navbar-style placeholder and arrow for consistency
const placeholderStyle = {
  color: "black",
  fontSize: "14px",
  fontFamily: "Roboto",
  fontWeight: 400,
  paddingLeft: "8px",
};

const customArrow = (
  <img
    src="/img/icons/dropdown-arrow.svg"
    alt="arrow"
    style={{
      width: "24px",
      height: "24px",
      marginTop: "3px",
      marginRight: "-6px",
    }}
  />
);

const Filters = ({
  searchParams,
  setSearchParams,
  paginationChange,
  setPaginationChange,
  limit,
  isDrawer = false,
  closeDrawer,
}) => {
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [categories, setCategories] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [cities, setCities] = useState([]);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}category/get/all/admin`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${user.sessionToken}`, // Uncomment and set token if needed
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch categories");
      }
      setCategories(
        data.data.map((item) => ({
          id: item._id,
          name: item.name,
          slug: item.slug,
        }))
      );
    } catch (err) {
      setError(err.message);
      console.error("Error fetching categories:", err);
    }
  };

  // Fetch event types
  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}eventType/get/all`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${user.sessionToken}`, // Uncomment and set token if needed
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch event types");
      }
      setEventTypes(
        data.data.map((item) => ({
          id: item._id,
          name: item.name,
          slug: item.slug,
        }))
      );
    } catch (err) {
      setError(err.message);
      console.error("Error fetching event types:", err);
    }
  };

  // Fetch cities
  const fetchCities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}city/getAllFilter`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${user.sessionToken}`, // Uncomment and set token if needed
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch cities");
      }
      setCities(
        data.data.map((item) => ({
          id: item._id,
          name: item.name,
        }))
      );
    } catch (err) {
      setError(err.message);
      console.error("Error fetching cities:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchEvents();
    fetchCities();
  }, []);

  const handleSearch = () => {
    // const params = new URLSearchParams(location.search);
    // params.set('page', '1');

    // navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    // setPaginationChange({ ...paginationChange, page: 1 });

    setSearchParams({
      ...Object.fromEntries(searchParams),
      search: searchTerm,
      page: "1",
    });

    // Close drawer on mobile after applying filter
    if (isDrawer && closeDrawer) {
      closeDrawer();
    }
  };

  const handleFilterChange = (key, value) => {
    const newParams = { ...Object.fromEntries(searchParams), page: "1" };
    if (value) {
      newParams[key] = value;
    } else {
      delete newParams[key];
    }
    setSearchParams(newParams);

    // Close drawer on mobile after applying filter
    if (isDrawer && closeDrawer) {
      closeDrawer();
    }
  };

  const removeFilter = (key) => {
    const newParams = { ...Object.fromEntries(searchParams), page: "1" };
    delete newParams[key];
    setSearchParams(newParams);

    // Close drawer on mobile after removing filter
    if (isDrawer && closeDrawer) {
      closeDrawer();
    }
  };

  const clearAllFilters = () => {
    setSearchParams({ page: "1", limit: limit });
    setSearchTerm("");

    // Close drawer on mobile after clearing filters
    if (isDrawer && closeDrawer) {
      closeDrawer();
    }
  };

  // Map slugs to names for displaying applied filters
  const getDisplayValue = (key, value) => {
    if (key === "category") {
      const category = categories.find((cat) => cat.slug === value);
      return category ? category.name : value;
    }
    if (key === "eventType") {
      const eventType = eventTypes.find((type) => type.slug === value);
      return eventType ? eventType.name : value;
    }
    if (key === "city") {
      // For cities, the value is already the name, so return it directly
      return value;
    }
    return value;
  };

  return (
    <div className={styles.filters}>
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.appliedFiltersHeader}>
        <h3>Applied Filters</h3>
        <Button
          type="link"
          onClick={clearAllFilters}
          className={styles.clearButton}
          disabled={
            !["category", "eventType", "city", "search"].some((key) =>
              searchParams.get(key)
            )
          }
        >
          CLEAR
        </Button>
      </div>
      <div className={styles.appliedFilters}>
        {["category", "eventType", "city", "search"].map((key) => {
          const value = searchParams.get(key);
          return value ? (
            <div key={key} className={styles.filterCard}>
              <span>
                {key.charAt(0).toUpperCase() + key.slice(1)}:{" "}
                {getDisplayValue(key, value)}
              </span>
              <button
                onClick={() => removeFilter(key)}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>
          ) : null;
        })}
      </div>
      <div className={styles.search}>
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search events..."
          className={`${styles.searchInput} ${styles.navbarStyleInput}`}
          onPressEnter={handleSearch}
        />
        <Button
          type="primary"
          onClick={handleSearch}
          className={`${styles.searchButton} ${styles.navbarStyleButton}`}
        >
          Search
        </Button>
      </div>
      <div className={styles.filterSection}>
        <h3>Categories</h3>
        <div className={styles.filterList}>
          {categories.length > 0 ? (
            categories.map((category) => (
              <div
                key={category.id}
                className={styles.filterItem}
                onClick={() =>
                  handleFilterChange(
                    "category",
                    searchParams.get("category") === category.slug
                      ? null
                      : category.slug
                  )
                }
              >
                <img
                  src={
                    searchParams.get("category") === category.slug
                      ? "/img/icons/checkboxRed.svg"
                      : "/img/icons/checkboxEmpty.svg"
                  }
                  alt={
                    searchParams.get("category") === category.slug
                      ? "Selected"
                      : "Unselected"
                  }
                  className={styles.checkboxImage}
                />
                <span>{category.name}</span>
              </div>
            ))
          ) : (
            <p>Loading categories...</p>
          )}
        </div>
      </div>
      <div className={styles.filterSection}>
        <h3>City</h3>
        <Select
          showSearch
          value={searchParams.get("city") || undefined}
          onChange={(value) => handleFilterChange("city", value)}
          className={styles.navbarStyleDropdown}
          suffixIcon={customArrow}
          placeholder={<span style={placeholderStyle}>Select City</span>}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {cities.length > 0 ? (
            cities.map((city) => (
              <Option key={city.id} value={city.name}>
                {city.name}
              </Option>
            ))
          ) : (
            <Option value="" disabled>
              Loading cities...
            </Option>
          )}
        </Select>
      </div>
      <div className={styles.filterSection}>
        <h3>Event Type</h3>
        <div className={styles.filterList}>
          {eventTypes.length > 0 ? (
            eventTypes.map((type) => (
              <div
                key={type.id}
                className={styles.filterItem}
                onClick={() =>
                  handleFilterChange(
                    "eventType",
                    searchParams.get("eventType") === type.slug
                      ? null
                      : type.slug
                  )
                }
              >
                <img
                  src={
                    searchParams.get("eventType") === type.slug
                      ? "/img/icons/checkboxRed.svg"
                      : "/img/icons/checkboxEmpty.svg"
                  }
                  alt={
                    searchParams.get("eventType") === type.slug
                      ? "Selected"
                      : "Unselected"
                  }
                  className={styles.checkboxImage}
                />
                <span>{type.name}</span>
              </div>
            ))
          ) : (
            <p>Loading event types...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Filters;
